import { MessageSquareReply, Send, StickyNote, X } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";

import type { MessageVisibility } from "../lib/types";
import { sanitizeRichHtml } from "../../shared/safeHtml";
import { FilePicker } from "./FilePicker";
import { startSendCountdown } from "../lib/sendCountdown";
import { RichTextEditor } from "./RichTextEditor";

export function parseCc(value: string): string[] {
  return [...new Set(value.split(/[;,\s]+/).map((email) => email.trim().toLowerCase()).filter(Boolean))].slice(0, 25);
}

export function Composer({
  mode = "public",
  allowInternal = false,
  allowCc = false,
  initialCc = [],
  busy = false,
  disabled = false,
  sendDelaySeconds = 0,
  placeholder,
  maxAttachmentBytes,
  richText = false,
  onSend,
  collapsedByDefault = true,
}: {
  mode?: MessageVisibility;
  allowInternal?: boolean;
  allowCc?: boolean;
  initialCc?: string[];
  busy?: boolean;
  disabled?: boolean;
  sendDelaySeconds?: 0 | 10;
  placeholder?: string;
  maxAttachmentBytes?: number;
  richText?: boolean;
  onSend: (input: { body: string; html?: string; visibility: MessageVisibility; files: File[]; cc: string[] }) => Promise<void>;
  collapsedByDefault?: boolean;
}) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [html, setHtml] = useState("");
  const [cc, setCc] = useState(initialCc.join(", "));
  const [expanded, setExpanded] = useState(!collapsedByDefault);
  const [visibility, setVisibility] = useState<MessageVisibility>(mode);
  const containerRef = useRef<HTMLDivElement>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sendError, setSendError] = useState("");
  const phase = useRef<"idle" | "waiting" | "sending">("idle");
  const cancelTimer = useRef<(() => void) | null>(null);
  const mounted = useRef(true);
  const locked = busy || disabled || submitting || remaining !== null;

  function cancelSend() {
    if (phase.current !== "waiting") return;
    cancelTimer.current?.();
    cancelTimer.current = null;
    phase.current = "idle";
    setRemaining(null);
  }

  useEffect(() => {
    mounted.current = true;
    const leave = () => cancelSend();
    window.addEventListener("pagehide", leave);
    return () => {
      mounted.current = false;
      cancelTimer.current?.();
      window.removeEventListener("pagehide", leave);
    };
  }, []);

  useEffect(() => {
    if (!expanded) setCc(initialCc.join(", "));
  }, [expanded, initialCc]);

  function open(nextVisibility: MessageVisibility = visibility) {
    setVisibility(nextVisibility);
    setExpanded(true);
    window.requestAnimationFrame(() => {
      containerRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      containerRef.current?.querySelector<HTMLElement>("[contenteditable='true'], textarea")?.focus();
    });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim() || locked || phase.current !== "idle") return;
    setSendError("");
    const input = {
      body: body.trim(),
      html: richText ? sanitizeRichHtml(html, 1_000_000) ?? undefined : undefined,
      visibility,
      files: [...files],
      cc: visibility === "public" && allowCc ? parseCc(cc) : [],
    };
    async function deliver() {
      if (!mounted.current) return;
      phase.current = "sending";
      cancelTimer.current = null;
      setRemaining(null);
      setSubmitting(true);
      try {
        await onSend(input);
        if (!mounted.current) return;
        setBody("");
        setHtml("");
        setFiles([]);
        setExpanded(false);
      } catch {
        if (mounted.current) setSendError("Sending failed. Your draft has been kept. Please try again.");
      } finally {
        phase.current = "idle";
        if (mounted.current) setSubmitting(false);
      }
    }
    if (sendDelaySeconds === 10) {
      phase.current = "waiting";
      cancelTimer.current = startSendCountdown(10, setRemaining, () => { void deliver(); });
    } else {
      void deliver();
    }
  }

  if (!expanded) return (
    <div className="composer-launcher" ref={containerRef}>
      <button type="button" className="composer-launcher__reply" onClick={() => open("public")}>
        <MessageSquareReply aria-hidden="true" size={18} />
        <span><strong>Reply to this conversation</strong><small>Click to write a formatted response</small></span>
      </button>
      {allowInternal && <button type="button" className="composer-launcher__note" onClick={() => open("internal")}><StickyNote aria-hidden="true" size={16} /> Add internal note</button>}
    </div>
  );

  return (
    <div className="composer-shell" ref={containerRef}>
      <form className={`composer composer--${visibility}`} onSubmit={submit}>
        {allowInternal ? (
          <div className="composer__mode" role="group" aria-label="Message visibility">
            <button type="button" disabled={locked} className={visibility === "public" ? "is-active" : ""} aria-pressed={visibility === "public"} onClick={() => setVisibility("public")}>Public reply</button>
            <button type="button" disabled={locked} className={visibility === "internal" ? "is-active" : ""} aria-pressed={visibility === "internal"} onClick={() => setVisibility("internal")}><StickyNote aria-hidden="true" size={14} /> Internal note</button>
            <button className="composer__collapse" type="button" aria-label="Collapse editor" title="Collapse editor" disabled={locked} onClick={() => setExpanded(false)}><X aria-hidden="true" size={17} /></button>
          </div>
        ) : (
          <div className="composer__heading"><strong>Reply</strong><button className="composer__collapse" type="button" aria-label="Collapse editor" title="Collapse editor" disabled={locked} onClick={() => setExpanded(false)}><X aria-hidden="true" size={17} /></button></div>
        )}
        {visibility === "internal" && <p className="composer__notice" role="status">Only your support team can see this note.</p>}
        {allowCc && visibility === "public" && (
          <label className="composer__cc">
            <span>Cc</span>
            <input disabled={locked} type="text" inputMode="email" value={cc} onChange={(event) => setCc(event.target.value)} placeholder="name@example.com, another@example.com" aria-label="Cc recipients" />
            <small>These recipients receive this message only. Remove them here to send without Cc.</small>
          </label>
        )}
        {richText ? (
          <RichTextEditor
            html={html}
            onChange={(nextHtml, text) => { setHtml(nextHtml); setBody(text); }}
            placeholder={placeholder ?? (visibility === "internal" ? "Add context for your team…" : "Write a reply…")}
            ariaLabel={visibility === "internal" ? "Internal note" : "Reply"}
            disabled={locked}
          />
        ) : (
          <><label className="sr-only" htmlFor="ticket-reply">{visibility === "internal" ? "Internal note" : "Reply"}</label><textarea id="ticket-reply" value={body} onChange={(event) => setBody(event.target.value)} placeholder={placeholder ?? (visibility === "internal" ? "Add context for your team…" : "Write a reply…")} rows={4} disabled={locked} /></>
        )}
        {sendError && <p className="composer__notice" role="alert">{sendError}</p>}
        {remaining !== null && <div className="composer__undo-send">
          <span role="status" aria-live="polite">Sending in {remaining}s…</span>
          <button className="button button--secondary button--small" type="button" onClick={cancelSend}>Cancel send</button>
        </div>}
        <div className="composer__footer">
          <FilePicker files={files} onChange={setFiles} maxBytes={maxAttachmentBytes} disabled={locked} />
          <button className="button button--primary composer__send" type="submit" disabled={locked || !body.trim()}><Send aria-hidden="true" size={17} /> {busy || submitting ? "Sending…" : visibility === "internal" ? "Add note" : "Send reply"}</button>
        </div>
      </form>
    </div>
  );
}
